import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { api } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Save } from 'lucide-react';
import { toast } from 'sonner';

const ACTIVITIES = [
  { id: 'fund_source', label: 'Fund Source', description: 'Account used for fund source transactions' },
  { id: 'loan_portfolio', label: 'Asset Transfer — Loan Portfolio', description: 'Maps to 1100 Loans and Advances' },
  { id: 'loan_loss_provision', label: 'Asset — Loan Loss Provision', description: 'Maps to 1101 Loan Loss Allowance' },
  { id: 'savings_control', label: 'Liability Transfer — Savings', description: 'Control account for savings deposits (2001)' },
  { id: 'fd_control', label: 'Liability — Fixed Deposits', description: 'Control account for FDs (2003)' },
  { id: 'share_capital', label: 'Equity — Share Capital', description: 'Maps to 3000 Share Capital' },
  { id: 'interest_income', label: 'Income from Interest', description: 'Maps to 4000 Interest Income' },
  { id: 'fee_income', label: 'Income from Fees', description: 'Maps to 4100 Fee Income' },
  { id: 'investment_income', label: 'Income from Investments', description: 'Maps to 4200 Investment Income' },
  { id: 'penalty_income', label: 'Income from Penalties', description: 'Penalty income account' },
  { id: 'interest_expense', label: 'Expense — Interest Paid', description: 'Maps to 5000 Interest Expense' },
  { id: 'write_off', label: 'Expense — Written Off', description: 'Maps to 5001 Write-Off' },
  { id: 'loan_loss_expense', label: 'Expense — Loan Loss Provision', description: 'Maps to 5100 Provision Expense' },
];

const FinancialActivitiesPage = () => {
  const [mappings, setMappings] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [mRes, aRes] = await Promise.all([api.get('/financial-activity-mappings'), api.get('/chart-of-accounts')]);
      setMappings(mRes.data || {});
      setAccounts((aRes.data || []).sort((a, b) => (a.account_code || '').localeCompare(b.account_code || '')));
    } catch (e) { setAccounts([]); }
    setLoading(false);
  };

  const handleSave = async () => {
    try { await api.put('/financial-activity-mappings', mappings); toast.success('Mappings saved'); }
    catch (e) { toast.error('Failed to save'); }
  };

  return (
    <AdminSubPage title="Accounts Linked to Financial Activities" breadcrumb={['Accounting', 'Financial Activity Accounts']} backPath="/dashboard/accounting">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">Map GL accounts to financial activities. This links system operations to the chart of accounts.</p>
        <Button onClick={handleSave} className="bg-[#1f1f1f] text-white text-sm gap-1.5" data-testid="fa-save"><Save className="w-4 h-4" /> Save Mappings</Button>
      </div>
      {loading ? <div className="text-center py-8 text-gray-400">Loading...</div> : (
        <div className="bg-white border border-gray-200 divide-y">
          {ACTIVITIES.map(a => (
            <div key={a.id} className="flex items-center justify-between px-6 py-4" data-testid={`fa-row-${a.id}`}>
              <div><div className="text-sm font-medium text-[#1f1f1f]">{a.label}</div><div className="text-xs text-gray-400">{a.description}</div></div>
              <div className="w-72">
                <Select value={mappings[a.id] || ''} onValueChange={v => setMappings({...mappings, [a.id]: v})}>
                  <SelectTrigger className="text-xs" data-testid={`fa-select-${a.id}`}><SelectValue placeholder="Select Account" /></SelectTrigger>
                  <SelectContent>
                    {accounts.map(acc => (
                      <SelectItem key={acc.account_code} value={acc.account_code}>
                        <span className="font-mono mr-2">{acc.account_code}</span>{acc.account_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminSubPage>
  );
};
export default FinancialActivitiesPage;
